import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsActivitiesMobileCombustionListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesDestroy,
  useCompanyReportsActivitiesMobileCombustionCreate,
  useCompanyReportsActivitiesMobileCombustionList,
  useCompanyReportsActivitiesMobileCombustionPreviewRetrieve,
  useCompanyReportsActivitiesMobileCombustionRetrieve,
  useCompanyReportsActivitiesMobileCombustionUpdate,
} from '@shared/api/lib/company-reports/company-reports';
import {
  CompanyReportsActivitiesMobileCombustionListParams,
  CompanyReportsActivitiesMobileCombustionPreviewRetrieveParams,
  MobileCombustionActivitiesList,
  MobileCombustionActivityRetrieve,
  PaginatedMobileCombustionActivitiesListList,
} from '@shared/api/types';

import { useQueryClient } from '@tanstack/react-query';

export const useGetMobileCombustionActivityList = (
  companyReportUuid: string,
  params?: CompanyReportsActivitiesMobileCombustionListParams
) => {
  return useCompanyReportsActivitiesMobileCombustionList<PaginatedMobileCombustionActivitiesListList>(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
        cacheTime: 1000 * 60 * 60 * 24,
      },
    }
  );
};

export const useCreateMobileCombustionActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesMobileCombustionCreate<MobileCombustionActivitiesList>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (data, variables) => {
          void refreshQueries(
            queryClient,
            getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
          );
          return refreshQueries(
            queryClient,
            getCompanyReportsActivitiesMobileCombustionListQueryKey(
              variables.companyReportUuid
            )
          );
        },
      },
    }
  );
};

export const useGetMobileCombustionActivity = ({
  mobileCombustionActivityUuid,
  reportUuid,
}: {
  mobileCombustionActivityUuid?: string;
  reportUuid: string;
}) => {
  return useCompanyReportsActivitiesMobileCombustionRetrieve<MobileCombustionActivityRetrieve>(
    reportUuid,
    mobileCombustionActivityUuid!,
    {
      request: useAxiosConfig(),
      query: {
        enabled: !!mobileCombustionActivityUuid,
      },
    }
  );
};

export const useUpdateMobileCombustionActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesMobileCombustionUpdate<MobileCombustionActivitiesList>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (data, variables) => {
          void refreshQueries(
            queryClient,
            getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
          );
          return refreshQueries(
            queryClient,
            getCompanyReportsActivitiesMobileCombustionListQueryKey(
              variables.companyReportUuid
            )
          );
        },
      },
    }
  );
};

export const useDeleteMobileCombustionActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesMobileCombustionListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useGetMobileCombustionPreview = ({
  companyReportUuid,
  params,
  enabled = true,
}: {
  companyReportUuid: string;
  params?: CompanyReportsActivitiesMobileCombustionPreviewRetrieveParams;
  enabled?: boolean;
}) => {
  return useCompanyReportsActivitiesMobileCombustionPreviewRetrieve(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        enabled: enabled,
        cacheTime: 0,
        staleTime: 0,
      },
    }
  );
};
